import { useEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";
import { FORMAT_ACTIONS, type FormatAction } from "../lib/format-actions";

type Position = "center" | { x: number; y: number };

type Props = {
  position: Position;
  /** Optional initial query so right-click on a word can scope the palette
   *  to e.g. just the strikethrough actions if the user pre-typed. */
  initialQuery?: string;
  onClose: () => void;
  onApply: (action: FormatAction) => void;
};

const MENU_W = 256;
const MENU_MAX_H = 360;

export function ShortcutsMenu({ position, initialQuery = "", onClose, onApply }: Props) {
  const [query, setQuery] = useState(initialQuery);
  const [activeIdx, setActiveIdx] = useState(0);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const listRef = useRef<HTMLUListElement | null>(null);

  // Score actions by how the query matches their id / label. Higher = better.
  const filtered = useMemo(() => {
    if (!query) return FORMAT_ACTIONS;
    const q = query.toLowerCase();
    const scored = FORMAT_ACTIONS.map((a) => {
      const id = a.id.toLowerCase();
      const label = a.label.toLowerCase();
      let score = -1;
      if (label === q || id === q) score = 100;
      else if (label.startsWith(q)) score = 80;
      else if (id.startsWith(q)) score = 75;
      else if (label.includes(q)) score = 40;
      else if (id.includes(q)) score = 35;
      return { a, score };
    });
    return scored
      .filter((x) => x.score >= 0)
      .sort((a, b) => b.score - a.score)
      .map((x) => x.a);
  }, [query]);

  useEffect(() => {
    setActiveIdx(0);
  }, [query]);

  // Outside click closes the menu.
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (menuRef.current?.contains(e.target as Node)) return;
      onClose();
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [onClose]);

  // Keyboard nav: arrow keys move selection, Enter applies, Esc closes.
  // Capture phase so we beat any other handlers.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        onClose();
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        e.stopPropagation();
        setActiveIdx((i) => Math.min(filtered.length - 1, i + 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        e.stopPropagation();
        setActiveIdx((i) => Math.max(0, i - 1));
      } else if (e.key === "Enter") {
        const action = filtered[activeIdx];
        if (action) {
          e.preventDefault();
          e.stopPropagation();
          onApply(action);
        }
      }
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [filtered, activeIdx, onApply, onClose]);

  // Keep highlighted item scrolled into view.
  useEffect(() => {
    const el = listRef.current?.children[activeIdx] as HTMLElement | undefined;
    el?.scrollIntoView({ block: "nearest" });
  }, [activeIdx]);

  // Position. For a point, clamp so the menu stays in the viewport.
  let style: React.CSSProperties;
  if (position === "center") {
    style = { left: "50%", top: "25vh", transform: "translateX(-50%)" };
  } else {
    const x = Math.min(position.x, window.innerWidth - MENU_W - 12);
    const y = Math.min(position.y, window.innerHeight - MENU_MAX_H - 12);
    style = { left: Math.max(12, x), top: Math.max(12, y) };
  }

  return (
    <div
      ref={menuRef}
      style={{ ...style, width: MENU_W }}
      className="fixed z-[60] overflow-hidden rounded-lg border border-border bg-surface shadow-modal"
      onMouseDown={(e) => e.stopPropagation()}
    >
      <input
        autoFocus
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="search…"
        className="input-bare w-full border-b border-border px-3 py-2 text-xs placeholder:text-ink-mute"
      />
      <ul
        ref={listRef}
        className="max-h-72 overflow-y-auto py-1"
        style={{ maxHeight: MENU_MAX_H - 38 }}
      >
        {filtered.length === 0 ? (
          <li className="px-3 py-2 text-xs text-ink-mute">no matches</li>
        ) : (
          filtered.map((a, i) => (
            <li
              key={a.id}
              onMouseEnter={() => setActiveIdx(i)}
              onMouseDown={(e) => {
                e.preventDefault();
                onApply(a);
              }}
              className={clsx(
                "flex cursor-pointer items-center gap-2.5 px-3 py-1.5 text-[12px]",
                i === activeIdx
                  ? "bg-lavender-50 text-ink"
                  : "text-ink-soft hover:bg-canvas hover:text-ink"
              )}
              title={a.label}
            >
              <span
                className={clsx(
                  "flex h-5 w-5 shrink-0 items-center justify-center rounded",
                  i === activeIdx ? "text-lavender-400" : "text-ink-soft"
                )}
              >
                {a.icon}
              </span>
              <span className="flex-1 truncate">{a.label}</span>
              <span className="shrink-0 font-mono text-[10px] text-ink-mute">
                {a.hint}
              </span>
            </li>
          ))
        )}
      </ul>
    </div>
  );
}
